import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogActions,
  Divider,
  FormControlLabel,
  Typography,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { o365gateway } from "@dashboard-v3/api";

type ConnectorType = o365gateway.ConnectorType;

type Params = {
  open: boolean;
  onClose: () => void;
  onSelect: (direction: ConnectorType[], saveAsDisabled: boolean) => void;
};

export default function FlowSelectionDialog({
  open,
  onClose,
  onSelect,
}: Params) {
  const { t } = useTranslation("gateway");
  const [outbound, setOutbound] = useState(false);
  const [outboundLoopback, setOutboundLoopback] = useState(false);
  const [inbound, setInbound] = useState(false);
  const [inboundLoopback, setInboundLoopback] = useState(false);
  const [saveAsDisabled, setSaveAsDisabled] = useState(true);

  const hasSelection = outbound || inbound;

  function handleConfirm() {
    const direction: ConnectorType[] = [];
    if (outbound) direction.push("Outbound");
    if (outboundLoopback) direction.push("OutboundLoopback");
    if (inbound) direction.push("Inbound");
    if (inboundLoopback) direction.push("InboundLoopback");
    onSelect(direction, saveAsDisabled);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h6">{t("flowModal.title")}</Typography>

          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box px={3} pb={1} mt={2}>
        <Box display="flex" flexDirection="column">
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={inbound}
                  onChange={e => {
                    setInbound(e.target.checked);
                    if (!e.target.checked) setInboundLoopback(false);
                  }}
                />
              }
              label={
                <Box>
                  <div>{t("flow.option.inbound.title")}</div>
                  <Typography variant="caption" color="textSecondary">
                    Configure inbound mail flow through mxHERO.
                  </Typography>
                </Box>
              }
            />
          </Box>
          {inbound && (
            <Box ml={4} mb={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={inboundLoopback}
                    onChange={e => setInboundLoopback(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <div>{t("flow.option.loopback")}</div>
                    <Typography variant="caption" color="textSecondary">
                      {t("flow.option.inboundLoopback.desc")}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          <Box mt={2} mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={outbound}
                  onChange={e => {
                    setOutbound(e.target.checked);
                    if (!e.target.checked) setOutboundLoopback(false);
                  }}
                />
              }
              label={
                <Box>
                  <div>{t("flow.option.outbound.title")}</div>
                  <Typography variant="caption" color="textSecondary">
                    Configure outbound mail flow through mxHERO.
                  </Typography>
                </Box>
              }
            />
            {outbound && (
              <Box ml={4} mb={4} mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={outboundLoopback}
                      onChange={e => setOutboundLoopback(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <div>{t("flow.option.loopback")}</div>
                      <Typography variant="caption" color="textSecondary">
                        {t("flow.option.outboundLoopback.desc")}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        <Divider style={{ marginTop: 8, marginBottom: 4 }} />

        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={saveAsDisabled}
              onChange={e => setSaveAsDisabled(e.target.checked)}
            />
          }
          label={t("flow.disabledCheck")}
        />
      </Box>

      <DialogActions>
        <Button onClick={onClose}>{t("common:cancel")}</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!hasSelection}
          onClick={handleConfirm}
          disableElevation
        >
          {t("common:confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const StyledTitle = styled(DialogTitle)`
  padding: 13px 60px 6px 20px;
  button {
    position: absolute;
    top: 0.3rem;
    right: 0.3rem;
    padding: 3px;
    .MuiSvgIcon-root {
      width: 21px;
      height: 21px;
    }
  }
`;
